import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import AppAppBar from '../components/AppAppBar';
import AppTheme from '../AppTheme';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

const STORAGE_KEY = 'anarchism_institute_posts_v1';
const DRAFT_STORAGE_KEY = 'anarchism_institute_draft_v1';

import config from '../config';
const server = config.server;
const API_BASE = `${server}/api`;

function formatTime(utc_str) {
  if (!utc_str) return '';
  const utc = new Date(utc_str);
  let offset = utc.getTimezoneOffset();
  let local = new Date(utc.getTime() - offset * 60000);

  const year = local.getFullYear()
  const month = (local.getMonth() + 1).toString().padStart(2, '0');
  const day = local.getDate().toString().padStart(2, '0');
  const hours = local.getHours().toString().padStart(2, '0');
  const minutes = local.getMinutes().toString().padStart(2, '0');
  
  return `${year}年${month}月${day}日 ${hours}:${minutes}`;
}

export default function InstitutePage(props) {
  const [posts, setPosts] = React.useState([]);
  const [author, setAuthor] = React.useState('');
  const [content, setContent] = React.useState('');
  const [charLeft, setCharLeft] = React.useState(800);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const authorRef = React.useRef(null);
  const [cooldown, setCooldown] = React.useState(0);

  // 簡單的滾動修復 - 只在組件掛載時執行一次
  React.useEffect(() => {
    // 只做最基本的修復，不要過度干預
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = 'auto';
    }
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.overflow = 'auto';
    }
    
    // 添加移動端滾動支持
    document.body.style.webkitOverflowScrolling = 'touch';
  }, []);

  // 初始化時從localStorage讀取草稿
  React.useEffect(() => {
    try {
      const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draft) {
        const { author: savedAuthor, content: savedContent } = JSON.parse(draft);
        if (savedAuthor) setAuthor(savedAuthor);
        if (savedContent) {
          setContent(savedContent);
          setCharLeft(800 - savedContent.length);
        }
      }
    } catch (e) {
      console.warn('讀取草稿失敗：', e);
    }
  }, []);

  // 自動保存草稿（防抖）
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (author || content) {
        try {
          localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
            author,
            content,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('保存草稿失敗：', e);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [author, content]);

  // Load posts function
  const loadPosts = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/posts?page=1&limit=20`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.posts)) {
        const formattedPosts = data.posts.map(post => ({
          id: post.id || Date.now() + Math.random(),
          author: post.name || '匿名',
          content: post.content,
          createdAt: new Date(post.datestamp).getTime()
        }));
        
        formattedPosts.sort((a, b) => b.createdAt - a.createdAt);
        setPosts(formattedPosts);
        
        console.log('Posts loaded:', formattedPosts.length);
      } else {
        console.error('Invalid response format:', data);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          if (Array.isArray(saved)) {
            setPosts(saved);
            console.log('Loaded posts from localStorage:', saved.length);
          }
        }
      } catch (e) {
        console.warn('Failed to load from localStorage:', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  React.useEffect(() => {
    if (posts.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
      } catch (e) {
        console.warn('保存留言失敗：', e);
      }
    }
  }, [posts]);

  // 處理署名輸入的函數
  const handleAuthorChange = (e) => {
    const rawValue = e.target.value;
    const cleanValue = rawValue.replace(/[^\u4e00-\u9fa5]/g, '');
    
    if (rawValue !== cleanValue) {
      e.preventDefault();
      e.target.value = cleanValue;
      setAuthor(cleanValue);
    } else {
      setAuthor(cleanValue);
    }
  };

  // 處理內容輸入的函數  
  const handleContentChange = (e) => {
    const value = e.target.value;
    if (value.length <= 800) {
      setContent(value);
      setCharLeft(800 - value.length);
    } else {
      const truncated = value.slice(0, 800);
      e.target.value = truncated;
      setContent(truncated);
      setCharLeft(0);
    }
  };
  
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Submit post function
  const onSubmit = async () => {
    if (cooldown > 0) return;

    const text = content.trim();
    if (!text) return;
    const name = author.trim() || '匿名';

    try {
      const response = await fetch(`${API_BASE}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          content: text
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setContent('');
        setCharLeft(800);
        setCooldown(60);
        
        try {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        } catch (e) {
          console.warn('刪除草稿失敗：', e);
        }
        
        await loadPosts();
        console.log('Post submitted successfully');
      } else {
        alert(data.error || 'Failed to post message');
      }
    } catch (error) {
      console.error('Failed to submit post:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        onSubmit();
      } else {
        e.preventDefault();
      }
    }
  };

  // 簡化的背景樣式
  const backgroundSx = {
    minHeight: '100vh',
    backgroundColor: '#f6f4ea',
    backgroundImage: `
      radial-gradient(1000px 600px at 20% -10%, rgba(255,255,255,0.7), rgba(255,255,255,0) 60%),
      radial-gradient(900px 500px at 120% 0%, rgba(255,255,255,0.55), rgba(255,255,255,0) 65%),
      linear-gradient(180deg, rgba(0,0,0,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
    `,
    backgroundSize: `auto, auto, 100% 28px, 28px 100%`,
    pb: { xs: 10, md: 14 },
  };

  const quote = `縱觀古今，人類歷次浩劫大都由極權政府釀成。我們，作為平民，人生短短幾十年，並沒有享受幾天安寧，反而往往因為政府的壓迫或提心吊膽或顛沛流離或生靈塗炭。我們都知道：一條曲線如果足夠短就可以近似為一條直線；同理：如果政府規模足夠小就可以近似為無政府狀態。事實上，那些所謂的政府職能都可以被公司或者公益組織（NGO）取代，而市場是契約的載體，我們應該充分尊重契約，而不是尊重權力。我們，作為上帝的兒女，能否進一步發揮我們的契約精神，在自由平等的基礎上構建一個僅依靠契約運行的世界，消弭一切形式的極權主義。`;

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <AppAppBar />
      
      <Box component="main" sx={backgroundSx}>
        <Container maxWidth="md" sx={{ pt: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Chip
              label="Anarchism Institute — Contract over Power"
              size="small"
              sx={{ mt: 1, color: '#546174', borderColor: '#bfc8d4' }}
              variant="outlined"
            />
          </Box>

          {/* 引言卡片 */}
          <Paper
            elevation={8}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 4,
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.06)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.86))',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontFamily: '"Noto Serif TC","Source Han Serif TC","Songti SC","STSong",serif',
                fontWeight: 600,
                color: '#1f2937',
              }}
            >
              引言
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography
              sx={{
                fontSize: { xs: '1.05rem', md: '1.1rem' },
                lineHeight: 1.95,
                color: '#2b3646',
                textAlign: 'justify',
              }}
            >
              {quote}
            </Typography>
          </Paper>

          {/* 輸入區 */}
          <Paper ref={inputRef} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="署名（可留空，預設匿名）"
                value={author}
                onChange={handleAuthorChange}
                inputRef={authorRef}
                fullWidth
                inputProps={{ maxLength: 20 }}
                helperText="署名只能包含中文字符"
              />

              <TextField
                label="發表你的觀點（支持 Ctrl/⌘ + Enter 發佈）"
                value={content}
                onChange={handleContentChange}
                onKeyDown={handleKeyDown}
                inputRef={contentRef}
                fullWidth
                helperText={`剩餘可輸入字數：${charLeft}`}
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  請理性交流，反對廣告與洗版；請勿曝光他人隱私（中共官員除外）；留言由AI演算法擇優展示。
                </Typography>
                <Button
                  variant="contained"
                  endIcon={cooldown === 0 ? <SendIcon /> : null}
                  onClick={onSubmit}
                  disabled={!content.trim() || cooldown > 0}
                >
                  {cooldown > 0 ? `冷卻中 (${cooldown}s)` : '發佈'}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* 留言列表 */}
          <Stack spacing={2}>
            {loading ? (
              <Typography align="center" sx={{ color: 'text.secondary', py: 6 }}>
                載入中...
              </Typography>
            ) : posts.length === 0 ? (
              <Typography align="center" sx={{ color: 'text.secondary', py: 6 }}>
                暫無留言；成為第一位提出見解的人吧。
              </Typography>
            ) : (
              posts.map((p) => (
                <Paper key={p.id} sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Avatar sx={{ bgcolor: '#253449' }}>{p.author.slice(0, 1)}</Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 600 }}>{p.author}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(p.createdAt)}
                        </Typography>
                      </Stack>
                      <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {p.content}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Container>
      </Box>
    </AppTheme>
  );
}